import React from "react";

import { Spacer } from "../Grid";
import { Heading } from "../Heading";
import { Illustration, IllustrationDictionary } from "../Illustration";
import { LegacyIllustrations } from "../Illustration/LegacyIllustrations";
import { Text } from "../Text";
import { UtilityText } from "../UtilityText";

import styles from "./ActionableCallout.scss";

export interface ActionableCalloutProps {
  /**
   * The footer/action content of the call out.
   */
  action?: React.ReactNode;
  /**
   * The heading of the callout
   */
  heading?: string;
  /**
   * The illustration you want to use. These are the keys of the illustrations in the `Illustrations` component
   */
  illustration?: IllustrationDictionary | "Stop" | "Unplugged";
  /**
   * The subheading string
   */
  subheading?: string;
  /**
   * Additional, optional piece to append below the subheading. Only renders if subheading is passed to us
   */
  subheadingDesc?: string;
  /**
   * A data-testid for the alert for easier upstream testing
   */
  testid?: string;
  /**
   * An info utility text you can add to the alert
   */
  info?: React.ReactNode | string;
  /**
   * The text below the subheading
   */
  text?: string;
}

/**
 * A large actionable callout with an illustration.
 */
export function ActionableCallout({
  action = null,
  heading = "",
  illustration = "Unplugged",
  subheading = "",
  subheadingDesc = "",
  testid = "actionable-callout",
  info = null,
  text = "",
}: ActionableCalloutProps) {
  let IllustrationComponent: React.ReactNode = null;

  // Supporting "Stop" and "Unplugged" from a separate illustration library for backwards compatibility
  switch (illustration) {
    case "Stop":
      IllustrationComponent = <LegacyIllustrations.Stop />;
      break;
    case "Unplugged":
      IllustrationComponent = <LegacyIllustrations.Unplugged />;
      break;
    default:
      IllustrationComponent = illustration ? (
        <Illustration illustration={illustration} size={196} />
      ) : null;
  }

  return (
    <div className={styles.wrapper} data-testid={testid}>
      {Boolean(illustration) && (
        <div className={styles.illustration} data-testid="callout-illustration">
          {IllustrationComponent}
        </div>
      )}
      {Boolean(heading) && (
        <Heading
          level="1"
          data-testid="callout-heading"
          className={`${styles.heading}`}
        >
          {heading}
        </Heading>
      )}
      {Boolean(subheading) && (
        <Heading
          level="2"
          data-testid="callout-subheading"
          className={`${styles.subheading}`}
        >
          {subheading}
          {Boolean(subheadingDesc) && (
            <>
              <Spacer size="tiny" />
              {subheadingDesc}
            </>
          )}
        </Heading>
      )}
      {Boolean(info) && (
        <UtilityText
          data-testid="callout-text"
          className={styles.info}
          size="small"
        >
          {info}
        </UtilityText>
      )}
      {Boolean(text) && (
        <Text data-testid="callout-text" paragraph className={styles.text}>
          {text}
        </Text>
      )}
      {Boolean(action) && (
        <div className={styles.action} data-testid="callout-action">
          {action}
        </div>
      )}
    </div>
  );
}
